import { Link } from 'react-router-dom';
import { useUserStatus } from '../../context/UserStatusContext';
import styles from './GatedFeature.module.css';

/**
 * A wrapper component that conditionally renders its children based on the user's plan.
 * If the user does not have access, it shows a "locked" overlay with an upgrade CTA.
 * @param {object} props
 * @param {string} props.requiredTier - The minimum plan required (e.g., 'premium', 'pro').
 * @param {React.ReactNode} props.children - The feature to be gated.
 */
const GatedFeature = ({ requiredTier, children, displayMode = 'overlay' }) => {
  const { userStatus } = useUserStatus();

  // A simple helper to check if the user has access.
  // This could be made more complex later (e.g., pro includes premium).
  const hasAccess =
    userStatus?.planTier === requiredTier ||
    userStatus?.planTier === 'pro' ||
    userStatus?.planTier === 'vip' ||
    userStatus?.planTier === 'founder';

  if (hasAccess) {
    return children;
  }

  if (displayMode === 'inline') {
    return (
      <div
        className={styles.inlineLocked}
        title={`Upgrade to ${requiredTier === 'premium' ? 'Premium' : 'Pro'} to unlock`}
      >
        {children}
        <i className={`fas fa-lock ${styles.inlineLockIcon}`}></i>
      </div>
    );
  }

  return (
    <div className={styles.lockedContainer}>
      <div className={styles.lockedOverlay}>
        <i className={`fas fa-lock ${styles.lockIcon}`}></i>
        <p>This is a {requiredTier} feature.</p>
        <Link to="/pricing" className="action-button secondary">
          Upgrade Now
        </Link>
      </div>
      {/* Render the children with a disabled style underneath the overlay */}
      <div className={styles.disabledContent}>{children}</div>
    </div>
  );
};

export default GatedFeature;
